/**
 * Frontend view-models for the JD self-assessment feature. These mirror the
 * shapes returned by the worker's `/api/...` assessment routes (via Hono RPC).
 * Expected answers are deliberately absent from `Question` — the server only
 * sends questions; answers come back as part of a completed assessment's
 * `review`, after submission.
 *
 * `Dimension` and `AnswerValue` are sourced from @my-app/shared (shared with
 * the extension + the typed API client) and re-exported here so existing
 * `import { Dimension } from '@/lib/assessment'` calls keep working.
 */

export type { AnswerValue, Dimension } from '@my-app/shared';
import type { AnswerValue, Dimension } from '@my-app/shared';

/** All three funnel dimensions in funnel order. */
export const ALL_DIMENSIONS: Dimension[] = [
  'willingness',
  'hard_requirements',
  'soft_requirements',
];

export type QuestionType = 'single_choice' | 'multiple_choice' | 'true_false';


export interface JDExtraction {
  jobTitle: string | null;
  jobDescription: string;
  hardRequirements: string;
  softRequirements: string;
  language: string;
}

export interface Question {
  dimension: Dimension;
  type: QuestionType;
  stem: string;
  options: string[];
}

export interface Quiz {
  id: string;
  jobId: string;
  jobTitle: string | null;
  language: string;
  extraction: JDExtraction;
  /** Which dimensions this quiz covers. Full quizzes = all three; a targeted
   *  practice quiz = a single dimension. */
  dimensions: Dimension[];
  willingness: Question[];
  hard: Question[];
  soft: Question[];
  numQuestions: number;
  model: string;
  createdAt: string;
  updatedAt: string;
}

export interface SectionScore {
  name: Dimension | '';
  earned: number;
  total: number;
  score: number;
  level: string;
}

export interface AssessmentSummary {
  id: string;
  quizId: string;
  /** Which dimensions this attempt actually assessed. Drives which score
   *  cards / badges render (un-assessed dimensions are hidden, not shown as 0). */
  dimensions: Dimension[];
  willingnessScore: SectionScore;
  hardScore: SectionScore;
  softScore: SectionScore;
  createdAt: string;
}

/** Breakdown status as reported by the summaries endpoint. */
export type BreakdownStatus = 'none' | 'pending' | 'ready' | 'failed';

/** One job's assessment status: its quiz + latest attempt (if any). */
export interface AssessmentSummaryItem {
  jobId: string;
  quizId: string;
  language: string;
  /** JD aspects extracted when the quiz was generated (basis for the questions). null if unavailable. */
  extraction: JDExtraction | null;
  /** Whether a JD breakdown has been run. 'ready' = extracted aspects available. */
  breakdownStatus: BreakdownStatus;
  attempts: number;
  /** True while a quiz regeneration is in flight (the list shows a spinner instead of the stale score). */
  generating?: boolean;
  /** True while a breakdown generation is in flight. */
  breakdownGenerating?: boolean;
  latest: AssessmentSummary | null;
}

export interface ReviewItem {
  dimension: Dimension;
  type: QuestionType;
  stem: string;
  options: string[];
  answer: AnswerValue;
  selected: AnswerValue;
}

export interface AssessmentDetail extends AssessmentSummary {
  answers: Array<{ index: number; selected: AnswerValue }>;
  review: ReviewItem[];
}

/** All questions in funnel order: willingness → hard → soft. */
export function allQuestions(quiz: Pick<Quiz, 'willingness' | 'hard' | 'soft'>): Question[] {
  return [...quiz.willingness, ...quiz.hard, ...quiz.soft];
}

/**
 * Reconstruct a takeable Quiz from a completed assessment's review, so a past
 * attempt can be RETAKEN against its original questions even after a newer quiz
 * has since been generated. The questions come back from `detail.review` in
 * funnel order; the result is submitted against `detail.quizId` so the server
 * scores it against the original quiz (the submit route keys off the `:quizId`
 * param and does not require it to be the latest). Fields the review doesn't
 * carry (extraction body, model, timestamps) are stubbed — only what
 * QuizSession needs to render and what submit needs (id/jobId) is populated.
 */
export function quizFromReview(
  detail: AssessmentDetail,
  opts: { jobId: string; jobTitle: string; language: string },
): Quiz {
  const toQuestion = (r: ReviewItem): Question => ({
    dimension: r.dimension,
    type: r.type,
    stem: r.stem,
    options: r.options,
  });
  return {
    id: detail.quizId,
    jobId: opts.jobId,
    jobTitle: opts.jobTitle,
    language: opts.language,
    extraction: {
      jobTitle: opts.jobTitle,
      jobDescription: '',
      hardRequirements: '',
      softRequirements: '',
      language: opts.language,
    },
    dimensions: detail.dimensions ?? ALL_DIMENSIONS,
    willingness: detail.review.filter((r) => r.dimension === 'willingness').map(toQuestion),
    hard: detail.review.filter((r) => r.dimension === 'hard_requirements').map(toQuestion),
    soft: detail.review.filter((r) => r.dimension === 'soft_requirements').map(toQuestion),
    numQuestions: detail.review.length,
    model: '',
    createdAt: detail.createdAt,
    updatedAt: detail.createdAt,
  };
}

import { Target, ShieldCheck, Handshake, type LucideIcon } from 'lucide-react';

export type DimensionMeta = {
  label: string;
  short: string;
  /** Monochrome outline icon — distinguishes dimensions by shape, not color,
   * for a sober, professional look (emojis read as too playful). */
  icon: LucideIcon;
  /** A single brand-aligned accent so the three dimensions read as a cohesive
   * set rather than a multi-color rainbow. */
  accent: string;
};

export const DIMENSION_META: Record<Dimension, DimensionMeta> = {
  willingness: {
    label: 'Willingness',
    short: 'Will',
    icon: Target,
    accent: 'text-primary',
  },
  hard_requirements: {
    label: 'Hard requirements',
    short: 'Hard',
    icon: ShieldCheck,
    accent: 'text-primary',
  },
  soft_requirements: {
    label: 'Soft requirements',
    short: 'Soft',
    icon: Handshake,
    accent: 'text-primary',
  },
};

/**
 * The SectionScore for a given dimension on a summary-like object, or null if
 * the dimension wasn't assessed (used to skip un-assessed dimensions in cards
 * / averages for single-dimension practice attempts).
 */
export function dimensionScore(
  s: { willingnessScore: SectionScore; hardScore: SectionScore; softScore: SectionScore },
  dim: Dimension,
): SectionScore | null {
  if (dim === 'willingness') return s.willingnessScore;
  if (dim === 'hard_requirements') return s.hardScore;
  return s.softScore;
}

/**
 * Average of the assessed dimension scores (0~100). Only dimensions listed in
 * `s.dimensions` are averaged, so a single-dimension practice attempt averages
 * just that dimension instead of diluting it with the un-assessed zeros.
 */
export function averageScore(s: {
  dimensions?: Dimension[];
  willingnessScore: SectionScore;
  hardScore: SectionScore;
  softScore: SectionScore;
}): number {
  const dims = s.dimensions && s.dimensions.length ? s.dimensions : ALL_DIMENSIONS;
  const scores = dims
    .map((d) => dimensionScore(s, d)?.score)
    .filter((v): v is number => typeof v === 'number');
  if (scores.length === 0) return 0;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

/** Tailwind text-color class by score band, shared across list/detail/results. */
export function scoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-sky-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-rose-600';
}
